// stores
import * as SponsorStore from '../../../redux/middleware/SponsorMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';

// state
import { AppState } from '../../../redux/reducers';
import { Sponsor } from '../../../models/Entities/Sponsor/Sponsor';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { SponsorContract } from '../../../models/Entities/Sponsor/SponsorContracts';
import { ErrorType } from '../../../models/HttpError';

//#region Props

interface IProps {
    SponsorId: number;
}

interface ConnectedProps {
    Sponsor: Sponsor | undefined;
    Contracts: Contract[];
    Protocols: Protocol[];
    IsLoading: boolean;
    IsLoadingContracts: boolean;
    IsLoadingProtocols: boolean;
    Error: ErrorType | undefined;
    AccessToken: string | undefined;
}

interface DispatchProps {
    GetSponsorById: typeof SponsorStore.actionCreators.GetSponsorById;
    GetAllContractAssociations: typeof SponsorStore.actionCreators.GetAllContractAssociations;
    GetAllContracts: typeof ContractStore.actionCreators.GetAllContracts;
    GetAllMedicalProtocols: typeof MedicalProtocolStore.actionCreators.GetAllMedicalProtocols;
}

export type Props = IProps & ConnectedProps & DispatchProps;

//#endregion

/** Format a numeric amount as a locale currency string. */
export const fmt = (value: number, currency: string): string => {
    try {
        return new Intl.NumberFormat('es-AR', {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    } catch {
        return `${currency} ${value.toFixed(2)}`;
    }
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => {
    const sponsor: Sponsor | undefined = state.Sponsor?.list?.find(
        (s: Sponsor) => s.id === ownProps.SponsorId
    );

    const contractIds =
        sponsor?.contractsAssociated?.map((sc: SponsorContract) => sc.contractId) ?? [];

    const contracts: Contract[] = (state.Contract?.list ?? []).filter((c: Contract) =>
        contractIds.includes(c.id)
    );

    const protocols: Protocol[] = (state.MedicalProtocols?.list ?? []).filter(
        (p: Protocol) => p.laboratoryId === ownProps.SponsorId
    ).sort((a: Protocol, b: Protocol) => a.title.localeCompare(b.title));

    return {
        Sponsor: sponsor,
        Contracts: contracts,
        Protocols: protocols,
        IsLoading:
            (state.Sponsor?.isLoadingAll ?? false) || (state.Contract?.isLoadingAll ?? false),
        IsLoadingContracts: (state.Sponsor as any)?.isLoadingAllContracts ?? false,
        IsLoadingProtocols: state.MedicalProtocols?.isLoadingAll ?? false,
        Error: state.Sponsor?.error,
        AccessToken: (state as any)?.oidc?.user?.access_token as string | undefined,
    };
};

export const mapDispatchToProps = {
    ...SponsorStore.actionCreators,
    GetAllContracts: ContractStore.actionCreators.GetAllContracts,
    GetAllMedicalProtocols: MedicalProtocolStore.actionCreators.GetAllMedicalProtocols,
};
